import * as React from 'react';
import { FC, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { FileOutlined, LinkOutlined, PictureOutlined } from '@ant-design/icons';
import { EmptyState } from '../empty_state/EmptyState';
import NotesMediaFilterPill from './NotesMediaFilterPill';
import NotesMediaList from './NotesMediaList';
import NotesMediaTiles from './NotesMediaTiles';
import { getFileMimeTypeFromString } from '../../utils/FileAccessUtils';
import { dateFormatterWithoutTimeZone } from '../../utils/DataFormatterUtils';
import { compareStrings } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	getNoteMediaAuthorName,
	NOTE_MEDIA_DATE_FORMAT,
	NoteMediaBuckets,
	NoteMediaItem,
} from '../../utils/NoteMediaUtils';

const MEDIA_FILTER_ALL = 'all';
const MEDIA_FILTER_MEDIA = 'media';
const MEDIA_FILTER_FILES = 'files';
const MEDIA_FILTER_LINKS = 'links';

const MEDIA_FILTERS: { value: string; label: string; icon?: React.ReactNode }[] = [
	{ value: MEDIA_FILTER_ALL, label: 'All' },
	{ value: MEDIA_FILTER_MEDIA, label: 'Media', icon: <PictureOutlined /> },
	{ value: MEDIA_FILTER_FILES, label: 'Files', icon: <FileOutlined /> },
	{ value: MEDIA_FILTER_LINKS, label: 'Links', icon: <LinkOutlined /> },
];

const getMediaCaption = (item: NoteMediaItem): string =>
	`${getNoteMediaAuthorName(item)} • ${dateFormatterWithoutTimeZone(
		item.addedAt,
		NOTE_MEDIA_DATE_FORMAT
	)}`;

interface NotesMediaEmptyStateProps {
	headline: string;
}

const NotesMediaEmptyState: FC<NotesMediaEmptyStateProps> = ({ headline }) => (
	<EmptyState
		graphic={<PictureOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />}
		headline={headline}
	/>
);

interface NotesMediaGridProps {
	/** Everything shared in the notes feed, already split by kind and ordered oldest first. */
	buckets: NoteMediaBuckets;
	userType: string;
}

/**
 * The Media, Files & Links view of a notes feed: every photo, video, voice note, attachment and
 * link shared in the conversation, behind a filter for each kind. The All view previews the photos
 * and videos on one scrollable row and lists the files and links together underneath.
 */
const NotesMediaGrid: FC<NotesMediaGridProps> = ({ buckets, userType }) => {
	const [activeFilter, setActiveFilter] = useState<string>(MEDIA_FILTER_ALL);
	const [videoInPreview, setVideoInPreview] = useState<NoteMediaItem | null>(null);
	const [mediaOverflowsStrip, setMediaOverflowsStrip] = useState(false);
	const mediaStripRef = useRef<HTMLDivElement>(null);

	const { media, files, links, filesAndLinks, total } = buckets;

	const handleSeeAllMedia = () => setActiveFilter(MEDIA_FILTER_MEDIA);
	const handleVideoPreviewClosed = () => setVideoInPreview(null);

	// "See all" only earns its place when the strip is actually hiding something. Tiles are a fixed
	// width, so that depends on how wide the panel is as much as on how many there are — hence a
	// measurement of the scrolling container rather than a count.
	useEffect(() => {
		const strip = mediaStripRef.current;
		if (!strip) {
			setMediaOverflowsStrip(false);
			return undefined;
		}

		const measureOverflow = () => setMediaOverflowsStrip(strip.scrollWidth > strip.clientWidth);

		measureOverflow();
		// Deferred to the next frame for the same reason CommonNotesChatPage defers its own observer:
		// changing state synchronously inside the callback trips "ResizeObserver loop completed with
		// undelivered notifications".
		const observer = new ResizeObserver(() => window.requestAnimationFrame(measureOverflow));
		observer.observe(strip);
		return () => observer.disconnect();
	}, [activeFilter, media.length]);

	if (total === 0) {
		return (
			<div className="flex min-h-0 flex-1 flex-col justify-center">
				<EmptyState
					graphic={<PictureOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />}
					headline="Nothing has been shared yet"
					body="Photos, videos, voice notes, files and links added to this conversation show up here."
				/>
			</div>
		);
	}

	const seeAllClassName = compareStrings(userType, ROLE_TYPES.SUPPLIER)
		? 'text-supplier'
		: 'text-buyer';

	const renderFilterPill = (filter: { value: string; label: string; icon?: React.ReactNode }) => (
		<NotesMediaFilterPill
			key={filter.value}
			value={filter.value}
			label={filter.label}
			icon={filter.icon}
			isActive={activeFilter === filter.value}
			userType={userType}
			onSelect={setActiveFilter}
		/>
	);

	return (
		// The filter row stays put while only the media below it scrolls.
		<div className="flex min-h-0 flex-1 flex-col">
			<div className="flex shrink-0 flex-row gap-2 px-4 pb-3 pt-4">
				{MEDIA_FILTERS.map(renderFilterPill)}
			</div>
			<div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-2">
				{activeFilter === MEDIA_FILTER_ALL ? (
					<>
						{media.length > 0 ? (
							<>
								<div className="mb-2 flex flex-row items-center justify-between">
									<span className="footnoteregular text-character-secondary">
										Photos &amp; Videos
									</span>
									{mediaOverflowsStrip ? (
										<button
											type="button"
											className={`footnoteregular cursor-pointer border-0 bg-transparent p-0 hover:underline ${seeAllClassName}`}
											onClick={handleSeeAllMedia}
										>
											See all
										</button>
									) : null}
								</div>
								<NotesMediaTiles
									items={media}
									variant="strip"
									onOpenVideo={setVideoInPreview}
									containerRef={mediaStripRef}
								/>
							</>
						) : null}
						{filesAndLinks.length > 0 ? <NotesMediaList items={filesAndLinks} /> : null}
					</>
				) : null}
				{activeFilter === MEDIA_FILTER_MEDIA ? (
					media.length > 0 ? (
						<NotesMediaTiles items={media} variant="grid" onOpenVideo={setVideoInPreview} />
					) : (
						<NotesMediaEmptyState headline="No photos or videos have been shared yet" />
					)
				) : null}
				{activeFilter === MEDIA_FILTER_FILES ? (
					files.length > 0 ? (
						<NotesMediaList items={files} />
					) : (
						<NotesMediaEmptyState headline="No files have been shared yet" />
					)
				) : null}
				{activeFilter === MEDIA_FILTER_LINKS ? (
					links.length > 0 ? (
						<NotesMediaList items={links} />
					) : (
						<NotesMediaEmptyState headline="No links have been shared yet" />
					)
				) : null}
			</div>
			{videoInPreview ? (
				<Modal
					visible={true}
					width={840}
					footer={null}
					title={getMediaCaption(videoInPreview)}
					onCancel={handleVideoPreviewClosed}
				>
					<video width="100%" height="auto" controls autoPlay>
						<source src={videoInPreview.url} type={getFileMimeTypeFromString(videoInPreview.url)} />
						Your browser does not support the video tag.
					</video>
				</Modal>
			) : null}
		</div>
	);
};

export default NotesMediaGrid;
